// modules/reportePagos/pages/ReportePagosPorServicioPage.jsx
// Migracion de busqueda.php (botones "Mostrar Reporte"/"Mes servicio" del
// bloque "Reporte del Año Servicio" -> seis / mes_servicio): cuanto se cobró
// por tipo de servicio (TKR/AVL/Scada/Seguridad/Ventas Varias/Energia/POC).
// A diferencia del reporte por Tipo/Banco, aca no importa si el cobro fue en
// efectivo Bs o $us: cada pago suma su monto (siempre en bolivianos) y su
// equivalente en dolares — igual que hacia el reporte original.
//
// Se le arreglo una inconsistencia real del PHP viejo: la vista anual
// ("seis") y la vista de un solo mes ("mes_servicio") traian dos listas de
// servicios DISTINTAS (una incluia Scada, la otra no) y solo la vista anual
// sumaba los cobros de franquicia sin factura. Ahora ambas vistas usan la
// misma lista de servicios activos (armada en vivo desde la tabla
// `servicio`) y la misma consulta (factura + franquicia directa).
import React, { useMemo, useState } from "react";
import {
  Box, Button, TextField, MenuItem, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  Tabs, Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { getReportePorServicio, getReporteMensualPorServicio } from "../services/reportePagosService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const bodySx = { "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } };

const SUCURSALES = ["Todo", "La Paz", "Cochabamba", "Santa Cruz"];
const MESES = [
  { value: "Todo", label: "Todo (año completo)" },
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];
const NOMBRES_MES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 12 }, (_, i) => anioActual + 1 - i);

const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Un color por servicio (igual idea que el reporte legado, que le daba una
// fila de color distinto a cada tipo de servicio para ubicarlos de un
// vistazo). Se define claro/oscuro por separado en vez de un solo alpha()
// para que sigan siendo legibles con texto oscuro en tema claro y con texto
// claro en tema oscuro.
const COLOR_SERVICIO_CLARO = {
  TKR: "#e9e3ab",
  AVL: "#f6c98a",
  Scada: "#f3c9e3",
  Seguridad: "#c6e8b3",
  "Ventas Varias": "#a9dcae",
  Energia: "#b7e2e6",
  POC: "#f2e6a3",
};
const COLOR_SERVICIO_OSCURO = {
  TKR: "#544f28",
  AVL: "#5c4322",
  Scada: "#54324a",
  Seguridad: "#2f4a29",
  "Ventas Varias": "#2a4a30",
  Energia: "#264749",
  POC: "#524c26",
};
const colorFilaServicio = (nombre) => (theme) =>
  (theme.palette.mode === "dark" ? COLOR_SERVICIO_OSCURO[nombre] : COLOR_SERVICIO_CLARO[nombre]) || "inherit";

const ReportePagosPorServicioPage = () => {
  const [tab, setTab] = useState(0);
  const [sucursal, setSucursal] = useState("Todo");
  const [year, setYear] = useState(anioActual);

  const [mes, setMes] = useState("Todo");
  const [resumen, setResumen] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [errorResumen, setErrorResumen] = useState("");
  const [buscadoResumen, setBuscadoResumen] = useState(false);

  const buscarResumen = async () => {
    setLoadingResumen(true);
    setErrorResumen("");
    try {
      const { data } = await getReportePorServicio({ sucursal, year, mes });
      setResumen(data);
      setBuscadoResumen(true);
    } catch (e) {
      setResumen(null);
      setErrorResumen(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoadingResumen(false);
    }
  };

  const [mensual, setMensual] = useState(null);
  const [loadingMensual, setLoadingMensual] = useState(false);
  const [errorMensual, setErrorMensual] = useState("");
  const [buscadoMensual, setBuscadoMensual] = useState(false);

  const buscarMensual = async () => {
    setLoadingMensual(true);
    setErrorMensual("");
    try {
      const { data } = await getReporteMensualPorServicio({ sucursal, year });
      setMensual(data);
      setBuscadoMensual(true);
    } catch (e) {
      setMensual(null);
      setErrorMensual(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoadingMensual(false);
    }
  };

  const totalMensual = useMemo(() => {
    if (!mensual) return null;
    const meses = Array.from({ length: 12 }, () => ({ bolivianos: 0, dolares: 0 }));
    const total = { bolivianos: 0, dolares: 0 };
    for (const fila of mensual.filas) {
      fila.meses.forEach((m, idx) => {
        meses[idx].bolivianos += m.bolivianos;
        meses[idx].dolares += m.dolares;
      });
      total.bolivianos += fila.total.bolivianos;
      total.dolares += fila.total.dolares;
    }
    return { meses, total };
  }, [mensual]);

  return (
    <DashboardLayout>
      <PageHeader
        title="Reporte por Servicio"
        subtitle="Cuánto se cobró por tipo de servicio (TKR, AVL, Scada, Seguridad, etc.)"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: 12, fontWeight: 700, textTransform: "none" } }}
          >
            <Tab label="Resumen" />
            <Tab label="Detalle Mensual" />
          </Tabs>
        </Paper>

        {tab === 0 && (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
              <TextField
                select size="small" label="Sucursal" sx={{ ...compactFieldSx, width: 150 }}
                value={sucursal} onChange={(e) => setSucursal(e.target.value)}
              >
                {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Mes" sx={{ ...compactFieldSx, width: 190 }}
                value={mes} onChange={(e) => setMes(e.target.value)}
              >
                {MESES.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.85rem" }}>{m.label}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Año" sx={{ ...compactFieldSx, width: 120 }}
                value={year} onChange={(e) => setYear(Number(e.target.value))}
              >
                {ANIOS.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>)}
              </TextField>
              <Button
                size="small" variant="contained"
                startIcon={loadingResumen ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                onClick={buscarResumen} disabled={loadingResumen} sx={{ minHeight: 32 }}
              >
                {loadingResumen ? "Buscando..." : "Buscar"}
              </Button>
            </Box>

            {errorResumen && <Alert severity="error" sx={{ mb: 2 }}>{errorResumen}</Alert>}

            {!buscadoResumen ? (
              <Alert severity="info">Elige sucursal, mes y año, y presiona Buscar.</Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" sx={bodySx}>
                    <TableHead>
                      <TableRow>
                        <StyledHeaderCell>Servicio</StyledHeaderCell>
                        <StyledHeaderCell align="right">Monto Bs</StyledHeaderCell>
                        <StyledHeaderCell align="right">Monto $us</StyledHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resumen.filas.map((f) => (
                        <TableRow key={f.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{f.nombre}</TableCell>
                          <TableCell align="right">{formatMoneda(f.bolivianos)}</TableCell>
                          <TableCell align="right">{formatMoneda(f.dolares)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(resumen.totales.bolivianos)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(resumen.totales.dolares)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
              <TextField
                select size="small" label="Sucursal" sx={{ ...compactFieldSx, width: 150 }}
                value={sucursal} onChange={(e) => setSucursal(e.target.value)}
              >
                {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Año" sx={{ ...compactFieldSx, width: 120 }}
                value={year} onChange={(e) => setYear(Number(e.target.value))}
              >
                {ANIOS.map((a) => <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>)}
              </TextField>
              <Button
                size="small" variant="contained"
                startIcon={loadingMensual ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                onClick={buscarMensual} disabled={loadingMensual} sx={{ minHeight: 32 }}
              >
                {loadingMensual ? "Buscando..." : "Buscar"}
              </Button>
            </Box>

            {errorMensual && <Alert severity="error" sx={{ mb: 2 }}>{errorMensual}</Alert>}

            {!buscadoMensual ? (
              <Alert severity="info">Elige sucursal y año, y presiona Buscar.</Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                  <Table size="small" sx={bodySx}>
                    <TableHead>
                      <TableRow>
                        <StyledHeaderCell>Servicio</StyledHeaderCell>
                        <StyledHeaderCell>Moneda</StyledHeaderCell>
                        {NOMBRES_MES_CORTOS.map((m) => (
                          <StyledHeaderCell key={m} align="right">{m}</StyledHeaderCell>
                        ))}
                        <StyledHeaderCell align="right">Total</StyledHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mensual.filas.map((fila) => {
                        const colorFila = colorFilaServicio(fila.nombre);
                        return (
                          <React.Fragment key={fila.id}>
                            <TableRow sx={{ backgroundColor: colorFila }}>
                              <TableCell rowSpan={2} sx={{ fontWeight: 600, backgroundColor: colorFila }}>{fila.nombre}</TableCell>
                              <TableCell>Bs.</TableCell>
                              {fila.meses.map((m, idx) => (
                                <TableCell key={idx} align="right">{formatMoneda(m.bolivianos)}</TableCell>
                              ))}
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoneda(fila.total.bolivianos)}</TableCell>
                            </TableRow>
                            <TableRow sx={{ backgroundColor: colorFila }}>
                              <TableCell>$us.</TableCell>
                              {fila.meses.map((m, idx) => (
                                <TableCell key={idx} align="right">{formatMoneda(m.dolares)}</TableCell>
                              ))}
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoneda(fila.total.dolares)}</TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                      {totalMensual && (
                        <>
                          <TableRow sx={{ backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[700] : t.palette.grey[300]) }}>
                            <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>Total Mes</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Bs.</TableCell>
                            {totalMensual.meses.map((m, idx) => (
                              <TableCell key={idx} align="right" sx={{ fontWeight: 700 }}>{formatMoneda(m.bolivianos)}</TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(totalMensual.total.bolivianos)}</TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[700] : t.palette.grey[300]) }}>
                            <TableCell sx={{ fontWeight: 700 }}>$us.</TableCell>
                            {totalMensual.meses.map((m, idx) => (
                              <TableCell key={idx} align="right" sx={{ fontWeight: 700 }}>{formatMoneda(m.dolares)}</TableCell>
                            ))}
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoneda(totalMensual.total.dolares)}</TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReportePagosPorServicioPage;
